import { defineStore } from "pinia";

import { getCompanyId } from "@/utils/userUtils";

import expensesApi from "@/services/api/expensesApi";

import { useNotificationStore } from "@/stores/notificationStore";

export const useExpenseStore = defineStore("expense", {
  state: () => ({
    expenses: [],
    selectItems: [],
    selectedExpense: null,
    companyId: getCompanyId(),
    dataIsLoading: false,
    isSubmitting: false,
  }),

  actions: {
    async fetchRepairExpenseOptions(vehicleId, search = "", repairId = null) {
      if (!vehicleId) return [];
      const records = [];
      let page = 1;
      let pages = 1;
      do {
        const response = await expensesApi.index(getCompanyId(), { page, limit: 100, "q[vehicle_id]": vehicleId, "q[comment]": search });
        records.push(...(response.data.result || []));
        pages = response.data.pagination_meta?.pages || 1;
        page += 1;
      } while (page <= pages);
      return records.filter((expense) => !expense.fleet_repair_id || String(expense.fleet_repair_id) === String(repairId));
    },

    normalizeResponseData(response) {
      return response.data.result || response.data || [];
    },

    async fetchExpenses(params = {}) {
      this.dataIsLoading = true;

      try {
        const response = await expensesApi.index(this.companyId, params);
        this.expenses = this.normalizeResponseData(response);
        return this.expenses;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchExpensesPage(params = {}) {
      this.dataIsLoading = true;

      try {
        const response = await expensesApi.index(this.companyId, params);
        const records = this.normalizeResponseData(response);
        const pagination = response.data.pagination_meta || {
          page: Number(params.page) || 1,
          pages: 1,
          series: [],
          count: records.length,
          limit: records.length,
        };

        return { records, pagination };
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchExpense(id) {
      this.dataIsLoading = true;

      try {
        const response = await expensesApi.show(this.companyId, id);
        this.selectedExpense = this.normalizeResponseData(response);
        return this.selectedExpense;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchExpenseSelectItems(params = {}) {
      this.dataIsLoading = true;

      try {
        const response = await expensesApi.index(this.companyId, params);
        this.selectItems = this.normalizeResponseData(response);
        return this.selectItems;
      } finally {
        this.dataIsLoading = false;
      }
    },

    async fetchExpenseSelectItem(id) {
      const exists = this.selectItems.find(
        (expense) => String(expense.id) === String(id)
      );
      if (exists) return exists;

      return await this.fetchExpense(id);
    },

    async createExpense(payload) {
      this.isSubmitting = true;

      try {
        const response = await expensesApi.create(this.companyId, payload);
        const expense = this.normalizeResponseData(response);
        useNotificationStore().showSuccess("Новый расход успешно создан");
        return expense;
      } finally {
        this.isSubmitting = false;
      }
    },

    async updateExpense(id, payload) {
      this.isSubmitting = true;

      try {
        const response = await expensesApi.update(
          this.companyId,
          id,
          payload
        );
        const expense = this.normalizeResponseData(response);
        useNotificationStore().showSuccess("Расход обновлён");
        return expense;
      } finally {
        this.isSubmitting = false;
      }
    },

    async acceptExpense(id) {
      this.isSubmitting = true;

      try {
        await expensesApi.accept(this.companyId, Number(id));
        useNotificationStore().showSuccess("Расход подтвержден");
      } finally {
        this.isSubmitting = false;
      }
    },

    async deleteExpense(id) {
      this.isSubmitting = true;

      try {
        await expensesApi.delete(this.companyId, id);
        useNotificationStore().showSuccess("Расход удалён");
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
